import { useEffect, useRef, useState } from "react";
import type { ReactElement } from "react";
import { ApiError } from "@kundeninfo/api-client";
import type { ApiClient, AdressAnsicht, AdressBesitzer,
              AdressListe as AdressListeAntwort } from "@kundeninfo/api-client";
import { useBestaetigung } from "@kundeninfo/ui";
import { Chip, Hinweis } from "@kundeninfo/ui/me-design";
import { formatDatum } from "../format.js";
import { KiKnopf } from "../components/meHuellen.js";
import { ObjektVerweis } from "../components/objektPanel/ObjektVerweis.js";
import { AdressPanel, adressWahlen, bezeichnung, rollenName, wahlFuer } from "./AdressPanel.js";
import { ADDRESS_TEXT } from "./addressTexts.js";
import type { AdressArt } from "./AdressPanel.js";
import "./AdressListe.css";

/**
 * Die ADRESSEN eines Objekts — EIN Baustein für drei Einbauorte: den Reiter „Adressen" der
 * Kundenakte, die Kontaktwege am Ansprechpartner und die Adress-Karte des eigenen
 * Unternehmens in der Verwaltung.
 *
 * Ein Baustein und nicht drei Abschriften: Die Regeln, die hier hängen (welche Art welche
 * Rolle trägt, dass die Hauptadresse oben steht, dass das Anlegen im strikten Panel läuft,
 * dass ein Kontextwechsel alles abräumt), gelten an allen drei Stellen gleich. Drei
 * Fassungen liefen beim ersten Nachbessern auseinander — und die unauffälligste davon wäre
 * die dritte, die niemand mehr anfasst. Was sich je Einbauort unterscheidet, steht in den
 * Eigenschaften: der Besitzer, sein Name für die Vorbelegung, das Schreibrecht.
 *
 * **Eigener CSS-Block `adr__`** — kein geteilter Blockname über Ansichten hinweg (Lektion
 * aus `.ka__`). Er gehört allein diesem Baustein (Liste und `AdressPanel.tsx`) und reist
 * mit ihm an jeden Einbauort.
 *
 * **Die Fachlichkeit steckt NICHT hier.** Welche Rolle zu welcher Art passt, ob eine ILN
 * ihre Prüfziffer hält, ob eine Hauptadresse gehen darf — das entscheidet der Server
 * (`domain/addressRules.ts`, `repositories/address.ts`). Diese Datei kennt weder die Paare
 * noch die Muster: Die wählbaren Arten und ihre Rollen kommen MIT der Liste
 * (`arten`), und jeder Befund des Servers findet hier seinen Weg an ein Feld. `web` darf
 * `domain` nicht importieren (scripts/check-boundaries.mjs) — und soll es auch nicht:
 * Eine zweite, abgeschriebene Regel wäre eine, die eines Tages anders urteilt als die
 * erste.
 *
 * **Das Panel** (Anlegen/Ändern) steht seit den Rechnungsangaben (Aufgabe 7) in
 * `AdressPanel.tsx` — die Liste öffnet es, übernimmt nach dem Speichern den Serverstand und
 * kümmert sich um Hauptadresse und Archivieren.
 */

/** Nur die Klientenmethoden, die dieser Baustein braucht — so lässt er sich im Test
 *  mit wenigen `vi.fn` zeichnen (Muster KundePortalMandanten). */
export type AdressClient = Pick<ApiClient,
  "listAddresses" | "createAddress" | "updateAddress" | "setPrimaryAddress"
  | "archiveAddress"
  // Die Ortszeile im Adresspanel (Spec „Adresszeile", R17).
  | "listLaender" | "listPlzOrte">;

type AdressRolle = AdressAnsicht["role"];

export type AdressListeProps = {
  client: AdressClient;
  besitzer: AdressBesitzer;
  /** Das Objekt, dessen Adressen hier stehen. Ein Wechsel setzt ALLES zurück. */
  ownerId: string;
  /** Überschrift und zugleich zugänglicher Name des Abschnitts. */
  titel: string;
  /** Der Name des Objekts (Firmenname, Ansprechpartner) — Vorbelegung der ersten
   *  Anschriftzeile beim Anlegen einer Postanschrift. Ohne Angabe bleibt sie leer. */
  namensVorschlag?: string;
  /** Ohne Schreibrecht (kein ADMIN am eigenen Unternehmen): keine Knöpfe, nur Text. */
  nurLesen?: boolean;
  /** Über diesem Abschnitt steht ein fremdes Panel — dann sind die Knöpfe gesperrt
   *  (dieselbe Überlegung wie `verdeckt` in KundeKonditionen). */
  verdeckt?: boolean;
  /** Nach jeder Änderung: Der Kopf der Kundenakte liest seine Hauptanschrift neu. */
  onGeaendert?: () => void;
};

/**
 * Die Hauptadresse ihrer Rolle steht oben (Hausregel „das Wichtigste zuerst").
 *
 * Der Server ordnet bereits so (`ORDNUNG` in repositories/address.ts), und gegen seine
 * Antwort ist das hier ein Leerlauf. Die Zusage gehört trotzdem in die Ansicht: Sie ist
 * eine Eigenschaft des BILDES, und ein Bild, das sich auf eine SQL-Zeile in einem anderen
 * Paket verlässt, hält sie nur so lange, wie niemand dort sortiert.
 *
 * Innerhalb einer Rolle, nicht innerhalb der Art: Eine Art trägt mehrere Rollen, und jede
 * hat ihre eigene Hauptadresse — die Rollen untereinander bleiben in der Reihenfolge, in
 * der der Server sie liefert (sein `sortIndex`).
 */
function hauptZuerst(liste: AdressAnsicht[]): AdressAnsicht[] {
  const rollen: AdressRolle[] = [];
  for (const e of liste) if (!rollen.includes(e.role)) rollen.push(e.role);
  return rollen.flatMap((r) => {
    const derRolle = liste.filter((e) => e.role === r);
    return [...derRolle.filter((e) => e.isPrimary), ...derRolle.filter((e) => !e.isPrimary)];
  });
}

/**
 * Eigene Adressen vor geerbten — UND je Herkunfts-Gruppe wieder die Hauptadresse oben
 * (Fixrunde 1, Ruling zu Aufgabe 6).
 *
 * `hauptZuerst` NACH der Herkunft anzuwenden wäre falsch gewesen: Eine geerbte
 * Hauptadresse hätte dann eine eigene Nebenadresse derselben Art/Rolle verdrängt — genau
 * das Gegenteil von „was am Objekt selbst steht, gilt vor dem, was von oben kommt"
 * (Global Constraint). Deshalb wird ZUERST nach Herkunft geteilt und ERST DANACH je
 * Teilliste sortiert: `[...hauptZuerst(eigene), ...hauptZuerst(geerbte)]`.
 */
function eigeneZuerst(a: AdressAnsicht[]): AdressAnsicht[] {
  const eigene = a.filter((e) => e.origin === null);
  const geerbte = a.filter((e) => e.origin !== null);
  return [...hauptZuerst(eigene), ...hauptZuerst(geerbte)];
}

export function AdressListe(p: AdressListeProps): ReactElement {
  /** `null` heisst „noch nicht geladen" — von „geladen und leer" zu unterscheiden. */
  const [eintraege, setEintraege] = useState<AdressAnsicht[] | null>(null);
  const [arten, setArten] = useState<AdressArt[]>([]);
  const [ladeFehler, setLadeFehler] = useState<string | null>(null);
  /** Das offene Panel: Ändern mit Vorlage, Anlegen mit `null`. */
  const [dialog, setDialog] = useState<{ vorlage: AdressAnsicht | null } | null>(null);
  /** Meldungen AUSSERHALB des Panels (Hauptadresse, Archivieren, Nachladen). Die des
   *  Speicherns führt das Panel selbst. */
  const [meldung, setMeldung] = useState<string | null>(null);

  /**
   * Die laufende NUMMER dieses Abschnitts — der Riegel gegen Antworten, die zu spät kommen
   * (Muster KundePortalMandanten). Jeder Abruf merkt sich beim Start die Nummer und
   * schreibt sein Ergebnis nur, wenn sie beim Eintreffen noch dieselbe ist. Der
   * Besitzerwechsel und das Abräumen zählen sie hoch und machen ALLE fliegenden Antworten
   * ungültig — auch die der Nachlade-Abrufe nach Speichern und Archivieren.
   */
  const lauf = useRef(0);
  const { client, besitzer, ownerId } = p;
  const listAddresses = client.listAddresses;

  useEffect(() => {
    const meiner = ++lauf.current;
    const nochAktuell = (): boolean => meiner === lauf.current;
    setEintraege(null);
    // Auch die ARTEN zurück: Sie gehören zum Besitzer (die Bestellnummer gibt es nur am
    // Beleg, `artenFuer` in routes/addresses.ts). Blieben die alten stehen, böte das
    // Anlege-Panel für einen Augenblick eine Art an, die dieser Besitzer nicht trägt.
    setArten([]);
    setLadeFehler(null);
    setDialog(null);
    setMeldung(null);
    listAddresses(besitzer, ownerId)
      .then((antwort) => {
        if (!nochAktuell()) return;
        setEintraege(antwort.items);
        setArten(antwort.arten);
      })
      .catch((fehler: unknown) => {
        if (!nochAktuell()) return;
        setEintraege([]);
        setLadeFehler(fehler instanceof ApiError ? fehler.message
          : "Die Adressen konnten nicht geladen werden.");
      });
    return () => { lauf.current += 1; };
  }, [listAddresses, besitzer, ownerId]);

  /**
   * Die Liste nach einer erfolgreichen Änderung neu holen — in EIGENER Obhut.
   *
   * Das Nachladen läuft bewusst nicht im `try` des Schreibwegs: Ein Fehler dort sähe aus
   * wie ein Fehler beim SCHREIBEN. Das Panel bliebe mit „Speichern" stehen, der Bearbeiter
   * drückte noch einmal — und legte die Adresse ein zweites Mal an. Deshalb ist die Sache
   * nach dem Schreiben ENTSCHIEDEN, und was hier schiefgeht, sagt genau das: Es ist
   * gespeichert, nur das Bild ist alt.
   */
  async function neuLaden(meiner: number, beiFehler: string): Promise<void> {
    try {
      const frisch = await listAddresses(besitzer, ownerId);
      if (meiner !== lauf.current) return;
      setEintraege(frisch.items);
      setArten(frisch.arten);
    } catch {
      if (meiner !== lauf.current) return;
      setMeldung(beiFehler);
    }
  }

  function oeffnen(vorlage: AdressAnsicht | null): void {
    setDialog({ vorlage });
    setMeldung(null);
  }

  /** „Neu laden" im Panel (409) bringt die frische Liste mit. Fehlt die geänderte Adresse
   *  darin, geht das Panel zu — und die Meldung sagt warum. */
  function beiListe(antwort: AdressListeAntwort): void {
    setEintraege(antwort.items);
    setArten(antwort.arten);
    const id = dialog?.vorlage?.id;
    if (id !== undefined && !antwort.items.some((x) => x.id === id)) {
      setDialog(null);
      setMeldung("Diese Adresse gibt es inzwischen nicht mehr.");
    }
  }

  const { bestaetigen } = useBestaetigung();
  async function beiHauptadresse(a: AdressAnsicht): Promise<void> {
    // Erst die Rückfrage (Entscheid 685) — wie „Hauptansprechpartner wechseln“: Den bisherigen Standard derselben Rolle
    // nennt sie mit.
    const bisher = (eintraege ?? []).find((x) => x.role === a.role && x.isPrimary && x.origin === null && x.id !== a.id);
    if (!(await bestaetigen({ titel: ADDRESS_TEXT.primaryTitle, bestaetigenText: ADDRESS_TEXT.primary,
      text: ADDRESS_TEXT.primaryText(bezeichnung(a), rollenName(a.role), bisher === undefined ? null : bezeichnung(bisher)),
    }))) return;
    const meiner = lauf.current;
    setMeldung(null);
    try {
      await client.setPrimaryAddress(besitzer, ownerId, a.id);
    } catch (fehler) {
      if (meiner !== lauf.current) return;
      setMeldung(fehler instanceof ApiError ? fehler.message
        : "Der Standard konnte nicht umgestellt werden.");
      return;
    }
    if (meiner !== lauf.current) return;
    p.onGeaendert?.();
    await neuLaden(meiner,
      "Der Standard steht. Die Liste konnte nicht neu geladen werden.");
  }

  async function beiArchivieren(a: AdressAnsicht): Promise<void> {
    // Die Rückfrage NENNT die Adresse: „Wirklich archivieren?" liesse offen, welche der
    // fünf Zeilen gemeint ist.
    if (!(await bestaetigen({
      titel: "Adresse archivieren?",
      text: `Adresse archivieren: ${bezeichnung(a)}. Sie verschwindet aus der Liste, bleibt auf festgeschriebenen Belegen aber erhalten. Ist sie an einer Kundenbeziehung als Rechnungsadresse oder Rechnungs-E-Mail festgelegt, gilt dort danach die Vorgabe. Wirklich archivieren?`,
      bestaetigenText: "Archivieren",
    }))) return;
    const meiner = lauf.current;
    setMeldung(null);
    try {
      await client.archiveAddress(besitzer, ownerId, a.id);
    } catch (fehler) {
      if (meiner !== lauf.current) return;
      // Der häufigste Fall ist die 422 HAUPTADRESSE_FEHLT: Sie ERKLÄRT, was zuerst zu tun
      // ist, und gehört deshalb im Wortlaut des Servers hierher. Dasselbe gilt für die
      // 422 ADRESSE_AM_BELEG_GEWAEHLT (Fixrunde nach Gesamtreview, I1): Sie NENNT die
      // Belege, an denen die Adresse hängt — ein eigener Satz hier könnte das nicht, weil
      // die Maske die Belege nicht kennt.
      setMeldung(fehler instanceof ApiError ? fehler.message
        : `${bezeichnung(a)} konnte nicht archiviert werden.`);
      return;
    }
    if (meiner !== lauf.current) return;
    p.onGeaendert?.();
    await neuLaden(meiner,
      "Archiviert. Die Liste konnte nicht neu geladen werden.");
  }

  const schreibbar = p.nurLesen !== true;
  const gesperrt = dialog !== null || p.verdeckt === true;
  const wahlen = adressWahlen(arten);

  return (
    <section className="adr" aria-label={p.titel}>
      <h3 className="adr__titel">{p.titel}</h3>

      {meldung !== null && dialog === null && <Hinweis art="fehler">{meldung}</Hinweis>}
      {ladeFehler !== null && <Hinweis art="fehler">{ladeFehler}</Hinweis>}
      {eintraege === null && <p className="adr__hinweis" role="status">Lädt …</p>}
      {eintraege !== null && eintraege.length === 0 && ladeFehler === null && (
        <p className="adr__hinweis">
          Für diesen Datensatz ist noch keine Adresse hinterlegt.
        </p>
      )}

      {eintraege !== null && wahlen
        .map((w) => ({ w, liste: eintraege.filter((e) => wahlFuer(wahlen, e.kind, e.role) === w) }))
        .filter(({ liste }) => liste.length > 0)
        .map(({ w, liste }) => (
          /* Eine Gruppe je WAHL (Entscheid 336): Postanschrift, Rechnungsadresse, E-Mail,
             Rechnungs-E-Mail … — sie ist die Ordnung, in der ein Mensch sucht („wo ist die
             Telefonnummer?"), nicht die Rolle. Die Rolle steht an der Zeile. */
          <div key={w.wert} className="adr__gruppe" role="group" aria-label={w.art.name}>
            <h4 className="adr__gruppentitel">{w.art.name}</h4>
            <ul className="adr__liste">
              {eigeneZuerst(liste).map((e) => (
                /* `key` am OBJEKT, nicht am Listenplatz: Die Liste ist sortiert und rückt
                   beim Archivieren zusammen. */
                <li key={e.id} className="adr__zeile">
                  <div className="adr__inhalt">
                    {e.zeilen.length > 0
                      ? e.zeilen.map((z, i) => (
                          <span key={`${e.id}-${i}`} className="adr__anschriftzeile">{z}</span>))
                      : <span className="adr__wert">{e.value}</span>}
                    {e.label !== null && (
                      <span className="adr__etikett">{e.label}</span>)}
                  </div>
                  <div className="adr__marken">
                    {/* Die Markierung ist ein WORT und nicht nur eine Farbe — sonst bliebe
                        sie für einen Bildschirmleser unsichtbar. */}
                    {/* Entscheid 349: „Standard“ nur, wenn die Rolle mehrere Adressen hat — bei einer
                        einzigen sagt die Markierung nichts. Entscheid 350: die Rolle steht immer da,
                        auch in den abgespaltenen Gruppen, damit alle Zeilen gleich aussehen. */}
                    {e.isPrimary && liste.filter((x) => x.role === e.role).length > 1 && (
                      <Chip className="adr__marke adr__marke--haupt">Standard</Chip>)}
                    <Chip className="adr__marke">{rollenName(e.role)}</Chip>
                    {e.validFrom !== null && (
                      <Chip className="adr__marke">ab {formatDatum(e.validFrom)}</Chip>)}
                  </div>
                  {e.origin !== null && (
                    <div className="adr__herkunft">
                      {/* Der Ursprungskunde ist ein fremdes Objekt (115, „Öffnen statt Springen“ E7): Text, der Sprung in
                          seine Akte direkt dahinter; ohne Gastgeber oder ohne Weg steht nur der Text. */}
                      <span>von {e.origin.companyName}</span>
                      <ObjektVerweis bezug={{ art: "akte", id: e.origin.customerId, reiter: "uebersicht", label: e.origin.companyName }} />
                    </div>
                  )}
                  {/* Eine geerbte Adresse ist hier sichtbar und nicht änderbar (Spec) —
                      und über diesen Kunden ohnehin nicht adressierbar (die Route findet
                      die Verknüpfung nicht an ihm und antwortete 404). */}
                  {schreibbar && e.origin === null && (
                    <div className="adr__aktionen">
                      <KiKnopf className="adr__knopf" deaktiviert={gesperrt}
                        name={`${bezeichnung(e)} ändern`}
                        onClick={() => oeffnen(e)}>Ändern</KiKnopf>
                      {!e.isPrimary && (
                        <KiKnopf className="adr__knopf" deaktiviert={gesperrt}
                          name={`${bezeichnung(e)} als Standard festlegen`}
                          onClick={() => { void beiHauptadresse(e); }}>
                          Als Standard
                        </KiKnopf>
                      )}
                      <KiKnopf className="adr__knopf" deaktiviert={gesperrt}
                        name={`${bezeichnung(e)} archivieren`}
                        onClick={() => { void beiArchivieren(e); }}>Archivieren</KiKnopf>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </div>
        ))}

      {schreibbar && (
        <div className="adr__fuss">
          <KiKnopf className="adr__knopf"
            deaktiviert={eintraege === null || gesperrt}
            onClick={() => oeffnen(null)}>Adresse hinzufügen</KiKnopf>
        </div>
      )}

      {dialog !== null && (
        <AdressPanel client={client} besitzer={besitzer} ownerId={ownerId} arten={arten}
          vorlage={dialog.vorlage}
          {...(p.namensVorschlag === undefined ? {} : { namensVorschlag: p.namensVorschlag })}
          onAbbrechen={() => setDialog(null)}
          onListe={beiListe}
          onGespeichert={() => {
            const meiner = lauf.current;
            // Das Panel geht SOFORT zu — die Adresse steht, und alles danach kann daran nichts
            // mehr ändern. Ein Panel, das bis zum Ende des Nachladens offen bliebe, lüde bei einem
            // klemmenden GET zum zweiten Klick auf „Speichern" ein: die Doppelanlage.
            setDialog(null);
            p.onGeaendert?.();
            // Nach dem Erfolg den SERVERSTAND holen statt die Antwort anzuhängen: Die Liste ist
            // sortiert (Hauptadresse zuoberst, dann Art und Rolle), und ein angehängter Satz
            // stünde am falschen Platz — bis zum nächsten Laden, das ihn wortlos verschöbe.
            void neuLaden(meiner, "Gespeichert. Die Liste konnte nicht neu geladen werden.");
          }} />
      )}
    </section>
  );
}
